import React, { useMemo, useEffect, useRef } from "react";
import { Icon, Button } from "semantic-ui-react";
import { isEmpty, size } from "lodash";
import { useMediaQuery } from "../useMediaQuery";
import {
  useTable,
  useRowSelect,
  useSortBy,
  useFilters,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import {
  GlobalFilter,
  DefaultColumnFilter,
  Pagination,
  SelectRowCheckBox
} from "../table";

const defaultPropGetter = () => ({})

const getClassName = (isEdit, newData, className) => {
  if (!isEmpty(isEdit) && newData.length < 6)
    return `${className} react-small-table`;
  return className;
};

const isSortedIcon = (column) => {
  if (column.isSortedDesc)
    return <Icon name="caret down" />;
  return <Icon name="caret up" />;
};

const createHeaderGroup = (headerGroups) => {
  return (headerGroups.map(headerGroup => {
    const { key, ...groupProps } = headerGroup.getHeaderGroupProps()
    return (<tr key={key} {...groupProps}>
      {headerGroup.headers.map(column => {
        const columnHeaderProps = column.getHeaderProps()
        const columnHeaderKey = columnHeaderProps.key
        const columnSpanProps = column.getSortByToggleProps()
        const columnSpanKey = columnSpanProps.key
        delete columnHeaderProps.key
        delete columnSpanProps.key
        return (<th key={columnHeaderKey} {...columnHeaderProps}>
          <div>
            <span key={columnSpanKey} {...columnSpanProps}>
              {column.render("Header")}
              {column.isSorted ? (
                isSortedIcon(column)
              ) : (
                ""
              )}
            </span>
          </div>
          <div>{column.canFilter ? column.render("Filter") : null}</div>
        </th>)
      })}
    </tr>)
  }
  ))
};

export const ReactTable = ({
  className = "",
  columns,
  data,
  hiddenColumns = [],
  mobileHiddenColumns = [],
  editable = false,
  renderChildRow,
  updateMyData,
  setOptions,
  options,
  skipReset,
  setEdit,
  isEdit = {},
  editSingleRow = false,
  hideGlobalFilter = false,
  initialGlobalFilter,
  showFooter = false,
  onSelectionChange,
  truncateCells = false,
  displayErrors = defaultPropGetter,
  getRowProps = defaultPropGetter,
  exportName = "",
  additionalData = "",
  additionalKey = "",
  includeExportColsOnly = false,
  exportCols = [],
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const activeHiddenColumns = useMemo(
    () => [...new Set([...hiddenColumns, ...(isMobile ? mobileHiddenColumns : [])])],
    [hiddenColumns, isMobile, mobileHiddenColumns]
  );

  const filterTypes = useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      }
    }),
    []
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  );

  const tableConfig = useMemo(
    () => ({
      columns,
      data,
      defaultColumn,
      updateMyData,
      setOptions,
      options,
      autoResetPage: !skipReset,
      autoResetSelectedRows: !skipReset,
      autoResetFilters: !skipReset,
      autoResetGlobalFilter: !skipReset,
      autoResetSortBy: !skipReset,
      filterTypes,
      initialState: {
        pageIndex: 0,
        hiddenColumns: activeHiddenColumns,
        globalFilter: initialGlobalFilter
      }
    }),
    [columns, data, defaultColumn, updateMyData, setOptions, options, skipReset, filterTypes, activeHiddenColumns, initialGlobalFilter]
  );
  const instance = useTable(
    tableConfig,
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    hooks => {
      editable && (
        hooks.visibleColumns.push(columns => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                {!editSingleRow && (
                  <SelectRowCheckBox setEdit={setEdit} row="all" isEdit={isEdit} {...getToggleAllRowsSelectedProps()} />)}
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <SelectRowCheckBox setEdit={setEdit} row={row} editSingleRow={editSingleRow} isEdit={isEdit} {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },
          ...columns
        ])
      )
    }
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setGlobalFilter,
    preGlobalFilteredRows,
    page,
    visibleColumns,
    state: { pageSize, pageIndex, globalFilter, selectedRowIds },
    setAllFilters
  } = instance;

  const onSelectionChangeRef = useRef(onSelectionChange);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current(selectedRowIds);
    }
  }, [selectedRowIds]);

  const handleResetFilter = () => {
    setAllFilters([]);
    setGlobalFilter("");
  };

  return (
    <div className="mobile-friendly-table-wrapper">
      {!hideGlobalFilter && (
        <div className="global-filter" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '0.5rem' }}>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={!isEmpty(isEdit) ? "" : globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
          <Button onClick={handleResetFilter}>Clear Filters</Button>
        </div>
      )}
      <div className="mobile-friendly-table-div-wrapper">

        <div className="mobile-friendly-table-div">
          <table id="react-table" className={getClassName(isEdit, page, className)} {...getTableProps()}>
            <thead>{createHeaderGroup(headerGroups)}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                const rowProps = row.getRowProps(getRowProps(row))
                const rowKey = rowProps.key
                delete rowProps.key
                return (
                  <React.Fragment key={i}>
                    <tr key={rowKey} {...rowProps}>
                      {row.cells.map((cell, index) => {
                        const cellProps = cell.getCellProps()
                        const cellKey = cellProps.key
                        delete cellProps.key
                        const cellTitle = truncateCells && typeof cell.value === 'string' ? cell.value : undefined;
                        return <td key={cellKey} title={cellTitle} {...cellProps}>{cell.render("Cell")}</td>;
                      })}
                    </tr>
                    {row.isExpanded ? (
                      <tr key={i}>
                        <td key={i + "cell"} colSpan={visibleColumns.length}>{renderChildRow({ row })}</td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
            {showFooter && (
              <tfoot>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <td {...column.getHeaderProps()}>{column.render("Header")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
        <div className="row-counts">
          <span>Total Rows: {size(preGlobalFilteredRows)}</span>
          {editable && <span className="row-count-span">Rows Selected: {editSingleRow && !isEmpty(selectedRowIds) ? 1 : size(selectedRowIds)}</span>}
        </div>
        <Pagination {...instance} pageIndex={pageIndex} pageSize={pageSize} />
      </div>
    </div>
  );
};
